import { getCursorShift, getDistanceAndRotateBetweenPoints, getPlaygroundValues, getRealPosition, validatePosition } from 'containers/Playground/utils/playground'
import { updatePlace } from '../playgroundSlice'
import React, { useRef, useState } from 'react'
import { getPreviousRef } from 'utils/nodes'
import { useReduxDispatch } from 'store'
import Point from './FramePoint'

interface FrameLineProps {
  borderPoints: boolean
  distance: number
  rotate: number
  idx: number
  position: playground.Frame
  previous: playground.Frame
  next: playground.Frame
  listRef: React.RefObject<HTMLDivElement>
  dbClickAction: basic.ReactMouseHandler
}

const FrameLine: React.FC<FrameLineProps> = ({ borderPoints, idx, listRef, position, previous, next, distance, rotate, dbClickAction }) => {

  const [isExtraPoint, showExtraPoint] = useState(false)

  const lineRef = useRef(null)
  const extraLines = useRef<JSX.Element | null>(null)
  const firstExtraLineRef = useRef<HTMLDivElement>(null)
  const secondExtraLineRef = useRef<HTMLDivElement>(null)

  const dispatch = useReduxDispatch()

  const removePoint = () => dispatch(updatePlace({ idx, remove: 1 }))
  const addPoint = (element: object) => dispatch(updatePlace({ idx: idx + 1, element }))
  const updatePoint = (element: object) => dispatch(updatePlace({ idx: idx, remove: 1, element }))

  const handleMouse = (e: React.MouseEvent, addNewPoint = false) => {
    const { clientX: initialX, clientY: initialY } = e
    const { scale, maxWidth, maxHeight, offsetLeft, offsetTop } = getPlaygroundValues()
    const x = addNewPoint ? getRealPosition(initialX, offsetLeft, scale) : position[0]
    const y = addNewPoint ? getRealPosition(initialY, offsetTop, scale) : position[1]
    const previousRef = !addNewPoint && listRef.current && lineRef.current && getPreviousRef(listRef.current.childNodes, lineRef.current)
    const startPos = addNewPoint ? position : previous
    let shiftX = 0
    let shiftY = 0

    const mouseMove = (e: MouseEvent) => {
      e.preventDefault()
      let possibleXShift = getCursorShift(initialX, e.clientX, scale)
      let possibleYShift = getCursorShift(initialY, e.clientY, scale)
      possibleXShift = validatePosition(possibleXShift + x, 0, maxWidth, false) - x
      possibleYShift = validatePosition(possibleYShift + y, 0, maxHeight, false) - y

      if (possibleXShift !== shiftX || possibleYShift !== shiftY) {
        shiftX = possibleXShift
        shiftY = possibleYShift

        if (firstExtraLineRef.current && secondExtraLineRef.current) {
          const extraPos = [x + shiftX, y + shiftY]
          const p1 = getDistanceAndRotateBetweenPoints(startPos, extraPos)
          const p2 = getDistanceAndRotateBetweenPoints(extraPos, next)

          updateStyle(firstExtraLineRef, getStyle(p1.distance, p1.rotate))
          updateStyle(secondExtraLineRef, getStyle(p2.distance, p2.rotate, extraPos))
        }
      }
    }

    const mouseUp = () => {
      const element = [x + shiftX, y + shiftY]

      generateExtraLines()
      if (previousRef) hideOrShowPreviousLine(previousRef)
      addNewPoint ? addPoint(element) : updatePoint(element)
      window.removeEventListener('mousemove', mouseMove)
      window.removeEventListener('mouseup', mouseUp)
    }

    generateExtraLines(startPos, [x, y])
    if (previousRef) hideOrShowPreviousLine(previousRef, true)
    window.addEventListener('mousemove', mouseMove)
    window.addEventListener('mouseup', mouseUp)
  }

  const hideOrShowPreviousLine = (ref: HTMLDivElement, hide = false) => {
    if (hide) ref.style.opacity = '0.25'
    else ref.style.removeProperty('opacity')
  }

  const updateStyle = (ref: React.RefObject<HTMLDivElement>, styles: React.CSSProperties) => {
    Object.entries(styles).forEach(([k, v]) => {
      if (ref.current && (k === 'width' || k === 'rotate' || k === 'top' || k === 'left')) {
        ref.current.style[k] = v
      }
    })
  }

  const generateExtraLines = (startPos?: playground.Frame, extraPos?: playground.Frame) => {
    const generate = !!startPos && !!extraPos
    showExtraPoint(generate)

    if (generate) {
      const p1 = getDistanceAndRotateBetweenPoints(startPos, extraPos)
      const p2 = getDistanceAndRotateBetweenPoints(extraPos, next)

      const list = [{
        ...p1,
        ref: firstExtraLineRef,
        position: startPos,
      }, {
        ...p2,
        ref: secondExtraLineRef,
        position: extraPos,
      }]

      extraLines.current = <>
        {list.map(({ distance, rotate, position, ref }, idx) => <div
          key={idx}
          ref={ref}
          className='place__line --focused'
          style={getStyle(distance, rotate, position)}>
          <Point />
        </div>)}
      </>

    } else extraLines.current = null
  }

  const getStyle = (d: number, r: number, p?: playground.Frame, isEditMode = true, isDim?: boolean) => {
    const style = {
      width: `${isEditMode ? d - 24 : d}px`,
      rotate: `${r}deg`
    } as React.CSSProperties

    if (isDim) style.opacity = 0.25
    if (p) {
      style.top = `${p[1]}px`
      style.left = `${p[0]}px`
    }
    return style
  }

  return (
    <>
      {isExtraPoint && extraLines.current}

      <div
        ref={lineRef}
        onDoubleClick={dbClickAction}
        onMouseDown={e => borderPoints && handleMouse(e, true)}
        className={`place__line${borderPoints ? ' --focused' : ''}`}
        style={getStyle(distance, rotate, position, borderPoints, isExtraPoint)}>

        {borderPoints && <Point
          removePoint={removePoint}
          movePoint={handleMouse} />}
      </div>
    </>
  )
}

export default FrameLine